import { PrivateRoute } from '@codezo/auth';
import { AuthRoleEnum } from '@codezo/graphql';
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
	rememberNotificationDestination,
	takeNotificationDestination,
} from '../../lib/notification-destination';
import { NotificationLinkPage } from './NotificationLinkPage';

/**
 * Guards `/n/:type/:id` and makes sure the destination survives signing in.
 *
 * A notification link usually arrives by email on a device with no session, so
 * the sign-in detour is the normal path through here, not an error case.
 * `PrivateRoute` carries the path through a token *refresh* on its own; this
 * covers the case where the refresh fails and the user is sent to the public app
 * to sign in properly, which would otherwise drop them on the dashboard.
 */
export function NotificationLinkRoute() {
	const { isAuthenticated, user } = useAuth();
	const location = useLocation();

	// A layout effect so it lands before PrivateRoute redirects — both its
	// <Navigate> and its wrong-role bounce act in passive effects, which run
	// after this.
	useLayoutEffect(() => {
		if (!isAuthenticated) {
			rememberNotificationDestination(`${location.pathname}${location.search}`);
			return;
		}
		// Arriving here signed in means the destination was reached — either
		// directly or via a token refresh, which returns to this path without
		// ever passing /auth/callback. Only the callback consumes the stash, so
		// without this the entry would outlive its purpose and hijack the next
		// sign-in in this tab, sending the user to a notification they already read.
		takeNotificationDestination();
	}, [isAuthenticated, location.pathname, location.search]);

	return (
		<PrivateRoute
			isAuthenticated={isAuthenticated}
			user={user}
			requiredRole={AuthRoleEnum.Organization}
		>
			<NotificationLinkPage />
		</PrivateRoute>
	);
}
